import React, { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import ReactLoading from 'react-loading';
import { formatCurrency } from 'react-data-formatter';
import MuiDialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import api from 'api';
import Select from '../Select';
import Button from '../Button';
import {
  primary,
  middleGray,
  lightGray,
  darkGray,
  white,
  error as errorColor,
  sevenSurfaceYellow,
  sevenYellowWarm,
  sevenTextPrimary,
} from 'styles/colorProvider';

const AUTH_COOKIE = 'splitrisk-authtoken';

export const PRORATA_SETTINGS_URL = '/prorata/settings';
export const DUE_DAY_CURRENT_DATE = 'current_date';
export const DUE_DAY_OPTION_FIXED = 'fixed';
export const DUE_DAY_OPTION_CURRENT_DATE = 'current_date';
export const DEFAULT_DUE_DAY_FIXED_OPTIONS = [5, 10, 15, 20];
export const DEFAULT_PRORATA_ALERT_MESSAGE =
  '⚠ Atenção! Informamos que para esta seleção será realizado um cálculo proporcional em relação ao primeiro vencimento. O valor da primeira fatura será atualizado automaticamente.';

const DEFAULT_SETTINGS = {
  dueDayOptions: DEFAULT_DUE_DAY_FIXED_OPTIONS,
  alertMessage: DEFAULT_PRORATA_ALERT_MESSAGE,
  allowCurrentDate: true,
};

let settingsCache = null;
let settingsPromise = null;

const normalizeSettings = (data) => {
  const rawOptions = Array.isArray(data?.due_day_options) ? data.due_day_options : DEFAULT_DUE_DAY_FIXED_OPTIONS;
  const dueDayOptions = rawOptions
    .map((d) => parseInt(d, 10))
    .filter((d) => Number.isInteger(d) && d >= 1 && d <= 31);
  return {
    dueDayOptions: dueDayOptions.length ? dueDayOptions : DEFAULT_DUE_DAY_FIXED_OPTIONS,
    alertMessage:
      typeof data?.alert_message === 'string' && data.alert_message.trim() ? data.alert_message : DEFAULT_PRORATA_ALERT_MESSAGE,
    allowCurrentDate: data?.allow_current_date !== false,
  };
};

/** GET /prorata/settings com cache em módulo (uma chamada por sessão; em erro usa defaults sem cachear). */
export const loadProrataSettings = ({ force = false } = {}) => {
  if (settingsCache && !force) return Promise.resolve(settingsCache);
  if (settingsPromise && !force) return settingsPromise;
  settingsPromise = api({
    method: 'GET',
    url: PRORATA_SETTINGS_URL,
    headers: { Authorization: Cookies.get(AUTH_COOKIE) },
  })
    .then((resp) => {
      settingsCache = normalizeSettings(resp?.data);
      return settingsCache;
    })
    .catch(() => DEFAULT_SETTINGS)
    .finally(() => {
      settingsPromise = null;
    });
  return settingsPromise;
};

export const useProrataSettings = () => {
  const [settings, setSettings] = useState(settingsCache || DEFAULT_SETTINGS);
  const [loadingSettings, setLoadingSettings] = useState(!settingsCache);

  useEffect(() => {
    let cancelled = false;
    loadProrataSettings()
      .then((s) => {
        if (!cancelled) setSettings(s);
      })
      .finally(() => {
        if (!cancelled) setLoadingSettings(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { settings, loadingSettings };
};

export const getApiErrorMessage = (err, fallback = 'Não foi possível concluir a operação') =>
  err?.response?.data?.error || err?.response?.data?.message || fallback;

/** Preview válido: objeto sem `ok === false` (o `/prorata/preview` stateless não devolve `ok`). */
export const isPreviewOk = (preview) => preview != null && typeof preview === 'object' && preview.ok !== false;

export const isFixedDueDay = (dueDay) => dueDay != null && dueDay !== '' && dueDay !== DUE_DAY_CURRENT_DATE;

export const padDueDay = (day) => String(day).padStart(2, '0');

/** Converte a escolha do picker no par `due_day` / `due_day_option` esperado pela API. */
export const toDueDayPayload = (dueDay) => {
  if (dueDay === DUE_DAY_CURRENT_DATE) return { due_day: null, due_day_option: DUE_DAY_OPTION_CURRENT_DATE };
  if (!isFixedDueDay(dueDay)) return { due_day: null, due_day_option: null };
  return { due_day: parseInt(dueDay, 10), due_day_option: DUE_DAY_OPTION_FIXED };
};

/** Reconstrói a escolha do picker a partir de `due_day` / `due_day_option` gravados na proposta. */
export const fromDueDayPayload = (dueDay, dueDayOption) => {
  if (dueDayOption === DUE_DAY_OPTION_CURRENT_DATE) return DUE_DAY_CURRENT_DATE;
  if (dueDayOption === DUE_DAY_OPTION_FIXED && dueDay != null) return parseInt(dueDay, 10);
  if (dueDayOption == null && dueDay != null) return parseInt(dueDay, 10);
  return null;
};

export const describeDueDayChoice = (dueDay, dueDayOption) => {
  const choice = fromDueDayPayload(dueDay, dueDayOption);
  if (choice === DUE_DAY_CURRENT_DATE) return 'Data atual';
  if (isFixedDueDay(choice)) return `Data fixa (dia ${padDueDay(choice)})`;
  return null;
};

const normalizeDueDayForApi = (dueDay) => {
  if (dueDay === DUE_DAY_CURRENT_DATE) return DUE_DAY_CURRENT_DATE;
  if (dueDay == null || dueDay === '') return null;
  const n = typeof dueDay === 'number' ? dueDay : parseInt(dueDay, 10);
  return Number.isInteger(n) && !Number.isNaN(n) ? n : null;
};

const buildLocalPreview = (monthlyValue) => ({
  ok: true,
  local: true,
  keepsCurrentDueDay: true,
  extensionDays: 0,
  monthlyValue,
  firstInstallmentValue: monthlyValue,
});

function DueDayProrataPicker({
  previewUrl,
  buildPreviewBody = (dueDay) => ({ dueDay }),
  previewOnCurrentDate = true,
  monthlyValue,
  allowCurrentDate = true,
  currentDateLabel = 'Data atual',
  label = 'Data de vencimento',
  dueDay,
  onDueDayChange,
  onPreviewChange,
  disabled = false,
  showInlineAlert = false,
  selectWidth = '100%',
  selectHeight = '36px',
}) {
  const { settings, loadingSettings } = useProrataSettings();
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [pendingDueDay, setPendingDueDay] = useState(null);

  const canUseCurrentDate = allowCurrentDate && settings.allowCurrentDate;
  const options = [
    ...(canUseCurrentDate ? [{ value: DUE_DAY_CURRENT_DATE, label: currentDateLabel }] : []),
    ...settings.dueDayOptions.map((day) => ({ value: day, label: `Dia ${padDueDay(day)}` })),
  ];

  const emitDueDay = (value) => {
    if (onDueDayChange) onDueDayChange(value);
  };

  const emitPreview = (value) => {
    if (onPreviewChange) onPreviewChange(value);
  };

  useEffect(() => {
    const normalized = normalizeDueDayForApi(dueDay);
    const isCurrent = normalized === DUE_DAY_CURRENT_DATE;

    if (normalized == null || (isCurrent && !previewOnCurrentDate) || !previewUrl) {
      const local = normalized != null && monthlyValue != null ? buildLocalPreview(Number(monthlyValue)) : null;
      setPreview(local);
      setPreviewError(null);
      setLoadingPreview(false);
      emitPreview(local);
      return undefined;
    }

    let cancelled = false;
    setLoadingPreview(true);
    setPreviewError(null);

    api({
      method: 'POST',
      url: previewUrl,
      headers: {
        Authorization: Cookies.get(AUTH_COOKIE),
        'Content-Type': 'application/json',
      },
      data: buildPreviewBody(normalized),
    })
      .then((resp) => {
        if (cancelled) return;
        const data = resp?.data || null;
        setPreview(data);
        emitPreview(data);
      })
      .catch((err) => {
        if (cancelled) return;
        setPreview(null);
        setPreviewError(getApiErrorMessage(err, 'Não foi possível calcular o pró-rata.'));
        emitPreview(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingPreview(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewUrl, dueDay, monthlyValue, previewOnCurrentDate]);

  const handleSelectChange = (event) => {
    const raw = event.target.value;
    if (!raw) {
      emitDueDay(null);
      return;
    }
    if (raw === DUE_DAY_CURRENT_DATE) {
      emitDueDay(DUE_DAY_CURRENT_DATE);
      return;
    }
    // Data fixa: abre o alerta parametrizado antes de confirmar a escolha.
    setPendingDueDay(parseInt(raw, 10));
  };

  /** "Alterar": cancela a ação e volta para "Data atual" (ou limpa, quando a data atual não é permitida). */
  const handleAlertChange = () => {
    setPendingDueDay(null);
    emitDueDay(canUseCurrentDate ? DUE_DAY_CURRENT_DATE : null);
  };

  /** "Prosseguir": confirma a data fixa escolhida. */
  const handleAlertProceed = () => {
    const chosen = pendingDueDay;
    setPendingDueDay(null);
    if (chosen != null) emitDueDay(chosen);
  };

  const selectValue = dueDay == null || dueDay === '' ? '' : String(dueDay);
  const extensionDays = preview?.extensionDays;
  const firstInstallment = preview?.firstInstallmentValue ?? preview?.adjustedValue;
  const recurring = preview?.recurringValue ?? preview?.monthlyValue;
  const showSummary = !loadingPreview && isPreviewOk(preview) && (firstInstallment != null || recurring != null);

  const renderAlertBox = () => (
    <div
      style={{
        background: sevenSurfaceYellow,
        border: `1px solid ${sevenYellowWarm}`,
        borderRadius: 8,
        padding: 14,
      }}
    >
      <p style={{ margin: 0, fontSize: 13, color: sevenTextPrimary, lineHeight: 1.5, whiteSpace: 'pre-line' }}>
        {settings.alertMessage}
      </p>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Select
        label={label}
        value={selectValue}
        onChange={handleSelectChange}
        disabled={disabled || loadingSettings}
        width={selectWidth}
        height={selectHeight}
      >
        <option value="">Selecione...</option>
        {options.map((opt) => (
          <option key={String(opt.value)} value={String(opt.value)}>
            {opt.label}
          </option>
        ))}
      </Select>

      {showInlineAlert ? renderAlertBox() : null}

      {loadingPreview ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0' }}>
          <ReactLoading color={primary} height={28} width={28} type="spin" />
        </div>
      ) : null}

      {previewError ? (
        <p style={{ margin: 0, fontSize: 12, color: errorColor, lineHeight: 1.45 }}>{previewError}</p>
      ) : null}

      {showSummary ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '6px 16px',
            fontSize: 13,
            color: middleGray,
            padding: '10px 0 0',
            borderTop: `1px solid ${lightGray}`,
          }}
        >
          <span>
            1ª parcela:{' '}
            <strong style={{ color: darkGray }}>{firstInstallment != null ? formatCurrency(firstInstallment) : '-'}</strong>
          </span>
          <span>
            Demais parcelas: <strong style={{ color: darkGray }}>{recurring != null ? formatCurrency(recurring) : '-'}</strong>
          </span>
          {extensionDays != null && Number(extensionDays) > 0 ? (
            <span style={{ gridColumn: '1 / -1' }}>
              Dias de extensão: <strong style={{ color: darkGray }}>{extensionDays}</strong>
            </span>
          ) : null}
        </div>
      ) : null}

      <MuiDialog open={pendingDueDay != null} onClose={handleAlertChange} maxWidth="xs" fullWidth>
        <DialogContent style={{ padding: 24 }}>
          <p style={{ margin: 0, fontSize: 14, color: sevenTextPrimary, lineHeight: 1.6, whiteSpace: 'pre-line' }}>
            {settings.alertMessage}
          </p>
          {pendingDueDay != null ? (
            <p style={{ margin: '14px 0 0', fontSize: 13, color: middleGray }}>
              Data selecionada: <strong style={{ color: darkGray }}>dia {padDueDay(pendingDueDay)}</strong>
            </p>
          ) : null}
        </DialogContent>
        <DialogActions style={{ padding: '0 24px 20px', justifyContent: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Button height="36px" outlined onClick={handleAlertChange} style={{ minWidth: 120 }}>
            Alterar
          </Button>
          <Button height="36px" onClick={handleAlertProceed} style={{ minWidth: 140, color: white }}>
            Prosseguir
          </Button>
        </DialogActions>
      </MuiDialog>
    </div>
  );
}

export default DueDayProrataPicker;
